/**
 * Sidebar Component
 *
 * Fixed left sidebar with collapsible mini-variant mode.
 * Provides main navigation for the application.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Drawer,
  Box,
  List,
  Typography,
  Button,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Logout,
  AdminPanelSettings,
  ListAlt,
} from '@mui/icons-material';
import { SidebarItem } from './SidebarItem';
import { SidebarParentItem } from './SidebarParentItem';
import { navigationItems } from '../utils/navigationItems';
import { useAuthContext } from '../auth/AuthContext';
import { useRole } from '../auth/useRole';

const DRAWER_WIDTH = 240;

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  mobileOpen = false,
  onMobileClose,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { account, logout } = useAuthContext();
  const { isSuperUser, isAtLeast } = useRole();

  const handleNewProject = () => {
    navigate('/projects/new');
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'primary.dark',
        color: '#ffffff',
      }}
    >
      {/* Logo/Brand */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 64,
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            color: '#ffffff',
          }}
        >
          📊 Beacon
        </Typography>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: 'auto', py: 2 }}>
        <List>
          {navigationItems
            .filter((item) => {
              if (item.path === '/settings') return isAtLeast('ACCOUNT_MANAGER');
              return true;
            })
            .map((item) =>
              item.children ? (
                <SidebarParentItem
                  key={item.path}
                  item={item}
                  onClick={handleNavClick}
                />
              ) : (
                <SidebarItem
                  key={item.path}
                  label={item.label}
                  path={item.path}
                  icon={item.icon}
                  onClick={handleNavClick}
                />
              )
            )}
          {isSuperUser && (
            <SidebarItem
              label="Admin"
              path="/admin/users"
              icon={<AdminPanelSettings />}
              onClick={handleNavClick}
            />
          )}
          {isSuperUser && (
            <SidebarItem
              label="Pipeline Logs"
              path="/admin/pipeline-logs"
              icon={<ListAlt />}
              onClick={handleNavClick}
            />
          )}
        </List>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* New Project Button — ACCOUNT_MANAGER and above only */}
      {isAtLeast('ACCOUNT_MANAGER') && (
        <Box sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            startIcon={<Add />}
            onClick={handleNewProject}
            sx={{
              justifyContent: 'center',
              px: 2,
            }}
          >
            New Project
          </Button>
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.12)' }} />

      {/* User info + Sign out */}
      <Box sx={{ p: 2 }}>
        {account && (
          <Typography
            variant="caption"
            noWrap
          sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 1 }}
          >
            {account.name || account.email}
          </Typography>
        )}
        <Button
          fullWidth
          size="small"
          startIcon={<Logout fontSize="small" />}
          onClick={() => void logout()}
          sx={{
            color: 'rgba(255,255,255,0.7)',
            justifyContent: 'flex-start',
            '&:hover': { color: '#ffffff', bgcolor: 'rgba(255,255,255,0.08)' },
          }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Drawer (Temporary) */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Desktop Drawer (Permanent) */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};
